<template>
  <!-- 系统维护提示条 -->
  <MaintenanceNotice />
  <div class="main">
    <div class="login">
      <div class="login-left">
        <img style="height: 100%" :src="loginLeftUrl" alt="loginLeft" />
      </div>
      <div class="login-right">
        <img style="height: 80px" :src="logoUrl" alt="logo" />
        <div class="login-right-title">{{ systemTitle }}</div>
        <div class="login-content">
          <div class="login-content-title">欢迎登录</div>
          <el-form
            style="width: 100%"
            :model="loginForm"
            label-width="70px"
            :rules="rules"
            ref="loginFormFef"
          >
            <el-form-item label="手机号" prop="phone">
              <el-input
                style="width: 100%"
                v-model="loginForm.phone"
                placeholder="请输入手机号"
                :prefix-icon="User"
                size="large"
              ></el-input>
            </el-form-item>
            <el-form-item label="密码" prop="password">
              <el-input
                style="width: 100%"
                type="password"
                v-model="loginForm.password"
                show-password
                placeholder="请输入密码"
                :prefix-icon="Lock"
                size="large"
              ></el-input>
            </el-form-item>
            <el-form-item v-if="needSmsCode" label="验证码" prop="smsCode">
              <PhoneSmsCode
                v-model:phone="loginForm.phone"
                v-model:smsCode="loginForm.smsCode"
                api-url="/api/login/sms"
                :show-phone="false"
                phone-prop=""
                sms-code-prop="smsCode"
                :phone-label="''"
                :sms-code-label="''"
                :phone-placeholder="''"
                sms-code-placeholder="请输入验证码"
              />
            </el-form-item>
          </el-form>
          <el-button class="login-content-btn" type="primary" @click="confirm"
            >登录</el-button
          >
          <div class="login-content-footer">
            <span class="btn" @click="router.push('/register')">注册账号</span>
            <span>|</span>
            <span class="btn" @click="checkPassword">忘记密码</span>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div class="footer">
    <a
      href="http://www.beian.gov.cn/portal/registerSystemInfo"
      rel="noreferrer"
      target="_blank"
      class="beian"
    >
      <span>京公网安备11010802045076</span>
    </a>
  </div>
  <ForgotPasswordDialog
    v-if="showForgotPasswordDialog"
    :visible="showForgotPasswordDialog"
    @update:visible="showForgotPasswordDialog = $event"
  />
</template>
<script setup lang="ts">
import { ref, onMounted } from "vue";
import { ElForm, ElMessage } from "element-plus";
import { User, Lock } from "@element-plus/icons-vue";
import { useRouter, useRoute } from "vue-router";
import { login } from "@/api/index";
import { ElLoading } from "element-plus";
import { setUserInfo, saveAccountToList } from "@/utils/index";
import MaintenanceNotice from "@/views/components/MaintenanceNotice.vue";
import { getSystemConfig } from "@/utils/index";
import PhoneSmsCode from "@/views/components/PhoneSmsCode.vue";
import ForgotPasswordDialog from "@/views/components/ForgotPasswordDialog.vue";

const {
  systemTitle,
  images: { logoUrl, loginLeftUrl },
  smsCodeEnabled,
} = getSystemConfig();

const router = useRouter();
const route = useRoute();

const loginFormFef = ref<typeof ElForm>();
const loginForm = ref({
  phone: "",
  password: "",
  smsCode: "",
});
const needSmsCode = ref(smsCodeEnabled);

// 从路由参数中获取账号信息并自动填充
onMounted(() => {
  if (route.query.phone) {
    loginForm.value.phone = route.query.phone as string;
  }
});
const rules = {
  phone: [{ required: true, message: "请输入用户名" }],
  password: [
    { required: true, message: "请输入密码" },
    { min: 6, message: "密码长度不能少于6位" },
  ],
  smsCode: [{ required: true, message: "请输入验证码" }],
};
const confirm = async () => {
  let loading: ReturnType<typeof ElLoading.service> | null = null;
  try {
    // 如果需要验证码，则验证验证码字段
    const validateFields = needSmsCode.value
      ? ["phone", "password", "smsCode"]
      : ["phone", "password"];
    const valid = await loginFormFef.value
      ?.validateField(validateFields)
      ?.catch(() => false);
    if (!valid) return;

    loading = ElLoading.service({
      lock: true,
      text: "正在登录...",
      background: "rgba(0, 0, 0, 0.7)",
    });

    const loginParams: {
      phone: string;
      password: string;
      sms_code?: string;
    } = {
      phone: loginForm.value.phone,
      password: loginForm.value.password,
    };

    // 如果需要验证码，则带上验证码
    if (needSmsCode.value) {
      loginParams.sms_code = loginForm.value.smsCode;
    }

    const res = await login(loginParams);

    if (res?.code === "success") {
      ElMessage({
        message: "登录成功",
        type: "success",
        center: true,
      });
      setUserInfo({
        token: res.content?.access_token || "",
        role: res.content?.role || "",
      });
      // 保存账号到列表（用于切换账号功能）
      saveAccountToList({
        phone: loginForm.value.phone,
        name: res.content?.name || (route.query.name as string) || "",
        role: res.content?.role || "",
      });

      const redirect = route.query.redirect || "/";
      router.push({ path: redirect as string });
    } else if (res?.code === "need_sms" && smsCodeEnabled) {
      // 需要验证码，显示验证码输入框
      needSmsCode.value = true;
      ElMessage({
        message: res.message || "请输入验证码完成登录",
        type: "warning",
        center: true,
      });
    } else if (res?.code === "error") {
      ElMessage({
        message: res.message,
        type: "error",
        center: true,
      });
    }
  } catch (error) {
    console.error((error as Error)?.message || "登录失败");
  } finally {
    loading?.close();
  }
};

const showForgotPasswordDialog = ref(false);

const checkPassword = () => {
  showForgotPasswordDialog.value = true;
};
</script>
<style lang="scss" scoped>
.main {
  height: 100vh;
  width: 100vw;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f0f2f5;
}

.login {
  width: 800px;
  height: 560px;
  display: flex;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  overflow: hidden;

  &-left {
    flex: 1;
    height: 100%;
    position: relative;
    overflow: hidden;

    img {
      border-radius: 10px;
      height: 100%;
      object-fit: cover;
    }
  }

  &-right {
    width: 420px;
    padding: 30px 40px;
    display: flex;
    flex-direction: column;
    align-items: center;

    &-title {
      font-size: 24px;
      font-weight: 600;
      color: #333;
      margin: 20px 0;
    }
  }

  &-content {
    width: 100%;
    max-width: 360px;

    &-title {
      font-size: 20px;
      font-weight: 500;
      color: #333;
      text-align: center;
      margin-bottom: 30px;
    }

    &-btn {
      width: 100%;
      height: 40px;
      margin-top: 20px;
    }

    &-footer {
      margin-top: 16px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      color: #666;
      font-size: 14px;

      .btn {
        cursor: pointer;
        color: #1890ff;
        transition: all 0.3s ease;

        &:hover {
          color: #40a9ff;
          text-decoration: underline;
        }
      }

      span:nth-child(2) {
        color: #d9d9d9;
        padding: 0 12px;
      }
    }
  }
}

:deep(.el-input__wrapper) {
  background: #f5f5f5;
  border: 1px solid #e8e8e8;
  border-radius: 4px;

  &:hover {
    border-color: #40a9ff;
  }

  &.is-focus {
    border-color: #1890ff;
    box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
  }
}

:deep(.el-form-item__label) {
  color: #666;
}

.footer {
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 50px;
  display: flex;
  justify-content: center;
  align-items: center;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(5px);
  box-shadow: 0 -1px 2px rgba(0, 0, 0, 0.05);

  .beian {
    display: flex;
    align-items: center;
    text-decoration: none;
    color: #666;
    font-size: 14px;
    transition: all 0.3s ease;

    &:hover {
      color: #333;
    }

    &-icon {
      width: 20px;
      height: 20px;
      margin-right: 8px;
    }
  }
}
</style>
